import { Link } from 'react-router-dom';
import { NAV_SECTIONS } from './nav-data';

const YEAR = new Date().getFullYear();

type FooterItem = { label: string; href: string; plain?: boolean };

const FOOTER_LINKS: Record<string, FooterItem[]> = {
  Product: [
    { label: 'Pricing', href: '/pricing' },
    { label: 'Compare', href: '/compare' },
    { label: 'Cost calculator', href: '/helpdesk-cost-calculator' },
    { label: 'Changelog', href: '/changelog' },
    { label: 'Roadmap', href: '/roadmap' },
    { label: 'Status', href: '/status' },
    { label: 'Helpdesk directory', href: '/p/helpdesks' },
  ],
  Solutions: (NAV_SECTIONS.find((x) => x.id === 'solutions')?.groups ?? []).flatMap((g) => g.items).map((i) => ({ label: i.label, href: i.to })),
  Guides: [
    { label: 'Pay-as-you-go helpdesk', href: '/pay-as-you-go-helpdesk' },
    { label: 'SLA tracking software', href: '/sla-tracking-software' },
    { label: 'AI ticketing system', href: '/ai-ticketing-system' },
    { label: 'AI helpdesk pay per resolution', href: '/ai-helpdesk-pay-per-resolution' },
    { label: 'AI reply drafting', href: '/ai-reply-drafting-software' },
    { label: 'AI customer support agent', href: '/ai-customer-support-agent' },
    { label: 'AI for customer service', href: '/ai-for-customer-service' },
    { label: 'Best helpdesk software', href: '/best-helpdesk-software' },
    { label: 'Issue tracking for startups', href: '/issue-tracking-for-startups' },
    { label: 'Knowledge base software', href: '/knowledge-base-software' },
    { label: 'Customer portal software', href: '/customer-portal-software' },
    { label: 'Helpdesk without per-seat pricing', href: '/helpdesk-without-per-seat-pricing' },
    { label: 'Internal ticketing software', href: '/internal-ticketing-software' },
    { label: 'Helpdesk automation', href: '/helpdesk-automation' },
    { label: 'Help desk software examples', href: '/help-desk-software-examples' },
    { label: 'Ticketing system examples', href: '/ticketing-system-examples' },
    { label: 'Zendesk alternative', href: '/zendesk-alternative' },
  ],
  Compare: [
    { label: 'vs Freshdesk', href: '/compare/kora-vs-freshdesk' },
    { label: 'vs Zendesk', href: '/compare/kora-vs-zendesk' },
    { label: 'vs Zoho Desk', href: '/compare/kora-vs-zoho-desk' },
    { label: 'vs Help Scout', href: '/compare/kora-vs-help-scout' },
    { label: 'vs Jira Service', href: '/compare/kora-vs-jira-service-management' },
    { label: 'All comparisons', href: '/compare' },
  ],
  Developers: [
    { label: 'REST API', href: '/developers/api' },
    { label: 'Webhooks', href: '/developers/webhooks' },
    { label: 'Embedded sessions', href: '/developers/embedded-sessions' },
    { label: 'API reference', href: '/docs' },
    { label: 'Rate limits', href: '/docs#rate-limits' },
  ],
  Company: [
    { label: 'About', href: '/about' },
    { label: 'Contact', href: '/contact' },
    { label: 'Privacy', href: '/privacy' },
    { label: 'Security', href: '/security' },
    { label: 'Terms', href: '/terms' },
    { label: 'Everything about Kora', href: '/know-all-about-kora', plain: true },
  ],
};

export function MarketingFooter() {
  return (
    <footer style={{ background: 'var(--warm-card)', borderTop: '1px solid var(--warm-border)' }}>
      <div className="max-w-[1200px] mx-auto px-5 sm:px-8 py-16">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-8">
          {/* Brand column */}
          <div className="col-span-2 md:col-span-3 lg:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-3">
              <img width={24} height={24} src="/assets/logo_square.svg" alt="" className="w-6 h-6 dark:invert" />
              <span className="font-display font-light text-lg" style={{ color: 'var(--warm-text)' }}>Kora</span>
            </Link>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--warm-muted)' }}>
              The pay-per-ticket AI helpdesk.
            </p>
          </div>

          {Object.entries(FOOTER_LINKS).map(([title, links]) => (
            <div key={title}>
              <p className="text-xs font-semibold tracking-wider uppercase mb-4" style={{ color: 'var(--warm-text)' }}>{title}</p>
              <ul className="space-y-2.5">
                {links.map((l) => (
                  <li key={l.href}>
                    {l.plain ? (
                      // Static HTML page served outside the React app, so a normal link (not client routing).
                      <a href={l.href} className="text-sm transition-colors" style={{ color: 'var(--warm-muted)' }}>{l.label}</a>
                    ) : (
                      <Link
                        to={l.href}
                        className="text-sm transition-colors"
                        style={{ color: 'var(--warm-muted)' }}
                        onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--warm-text)')}
                        onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--warm-muted)')}
                      >
                        {l.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-16 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs" style={{ borderTop: '1px solid var(--warm-border)', color: 'var(--warm-muted)' }}>
          <span>© {YEAR} Kora · EU-hosted · Pay per ticket</span>
          <Link to="/status" className="transition-colors hover:underline" style={{ color: 'var(--warm-muted)' }}>
            All systems operational →
          </Link>
        </div>
      </div>
    </footer>
  );
}
